import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, DataTable } from '../primitives';
import { SectionProps } from './types';

export const SourcesSection: React.FC<SectionProps> = ({
  assumptions,
  formatCurrency,
  citations
}) => {
  const marketReturns = [
    { metric: 'S&P 500 (long-term average)', return: '~10% / ~7.5%', source: 'NYU Stern', used: 'Stocks, Geography' },
    { metric: 'S&P 500 (Stocks Outperform)', return: '~11% / 8.5%', source: 'Assumption', used: 'Compare' },
    { metric: 'S&P 500 (Balanced)', return: '~10% / 7.5%', source: 'Historical avg', used: 'Compare' },
    { metric: 'S&P 500 (RE Outperforms)', return: '~9% / 6.5%', source: 'Assumption', used: 'Compare' },
    { metric: 'NASDAQ-100 (2010-2024)', return: '~17% / ~14%', source: 'NASDAQ', used: 'Stocks, Geography' },
    { metric: 'Bond Returns (assumed)', return: '~4% / ~1.5%', source: 'Model default', used: 'Sell' },
    { metric: 'Shiller CAPE Ratio (current)', return: '~40', source: 'Shiller', used: 'Stocks, Uncertainty' },
    { metric: 'Shiller CAPE Ratio (historical avg)', return: '~17', source: 'Since 1880', used: 'Stocks' },
  ];

  const realEstateReturns = [
    { market: 'US National (Case-Shiller)', return: '~5% / ~2.3%', source: 'FRED', used: 'Geography' },
    { market: 'LA Metro (Case-Shiller)', return: '~6% / ~3.5%', source: 'FRED', used: 'Geography, Compare' },
    { market: 'Newport Beach (2019-2024)', return: '~15-18% / ~12-15%', source: 'Zillow', used: 'Geography' },
    { market: 'Model: Conservative', return: '~4.8% / 2.3%', source: 'National avg', used: 'Compare' },
    { market: 'Model: Balanced', return: '~6% / 3.5%', source: 'LA/CA avg', used: 'Compare' },
    { market: 'Model: Optimistic', return: '~7% / 4.5%', source: 'Above LA avg', used: 'Compare' },
  ];

  const propertyInputs = [
    { input: 'Property Value', value: formatCurrency(assumptions.propertyValue), notes: 'Zillow / Redfin avg' },
    { input: 'Monthly Rent', value: formatCurrency(assumptions.monthlyRent), notes: 'Apartment List + comps' },
    { input: 'Gross Rent Yield', value: `${((assumptions.monthlyRent * 12 / assumptions.propertyValue) * 100).toFixed(1)}%`, notes: 'Annual rent / property value' },
    { input: 'Property Tax Rate', value: `${assumptions.propertyTaxRate}%`, notes: '1% Prop 13 base + ~0.1% local assessments (of assessed value, not market)' },
    { input: 'Annual Insurance', value: formatCurrency(assumptions.insuranceCost), notes: 'CA coastal (market in crisis)' },
    { input: 'Maintenance', value: `${assumptions.maintenanceRate}% of value`, notes: 'Routine annual maintenance (see sensitivity analysis)' },
    { input: 'Vacancy Rate', value: `${assumptions.vacancyRate}%`, notes: 'Low for desirable area' },
    { input: 'Management Fee', value: `${assumptions.managementFee}% of rent`, notes: 'If using property manager' },
    { input: 'Selling Costs', value: `${assumptions.sellingCosts}%`, notes: 'Agent fees + closing' },
    { input: 'Depreciation Period', value: '27.5 years', notes: 'IRS residential rental' },
    { input: 'Building % (for depreciation)', value: '25%', notes: 'Estimate for coastal Newport Beach (land ~75%). County assessor may differ—verify with tax records.' },
    { input: 'Rental Income Tax Rate', value: `${assumptions.rentalIncomeTaxRate}%`, notes: 'Fed + CA combined marginal' },
    { input: 'Capital Gains Tax Rate', value: `${assumptions.capitalGainsRate}%`, notes: 'Fed + CA combined (can be 24-37% depending on income)' },
  ];

  return (
    <ReportSection id="sources" title="Sources & Methodology" subtitle="Where this data comes from">
      <ReportSubsection title="Data Sources">
        <div className="space-y-3">
          {Object.values(citations).map((citation) => (
            <div key={citation.id} className="flex items-start gap-3 text-sm">
              <span className="text-gray-700 font-medium whitespace-nowrap min-w-[140px]">{citation.source}</span>
              {citation.url && (
                <a href={citation.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700 truncate">
                  {citation.url}
                </a>
              )}
            </div>
          ))}
        </div>
      </ReportSubsection>

      <ReportSubsection title="Methodology">
        <p className="text-gray-700 mb-3">
          This model projects wealth over a 10-year period using the following approach:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li>All values are shown in <strong>real (inflation-adjusted)</strong> terms</li>
          <li>Property appreciation and rent growth compound annually</li>
          <li>Rental income is assumed to be reinvested in the stock market</li>
          <li>Standard 27.5-year depreciation schedule using 25% building value (75% land)—appropriate for Newport Beach coastal property where land is the majority of value</li>
          <li>Combined 30% marginal tax rate on rental income; capital gains rates vary by income (federal 15-23.8% + CA 9-13% = 24-37% combined)</li>
          <li>80/20 stock/bond portfolio for the sell scenario (adjustable)</li>
        </ul>
      </ReportSubsection>

      <ReportSubsection title="Key Numbers Reference">
        <p className="text-gray-700 mb-4">
          Returns shown as <span className="font-medium" title="Nominal: before inflation adjustment">nominal</span> / <span className="font-medium" title="Real: after inflation adjustment">real</span> (inflation-adjusted).
          The model assumes 2.5% annual inflation.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6 mb-3">Market Return Assumptions</h4>
        <DataTable
          columns={[
            { key: 'metric', header: 'Metric', align: 'left' },
            { key: 'return', header: 'Return', align: 'right' },
            { key: 'source', header: 'Source', align: 'left', muted: true },
            { key: 'used', header: 'Used in', align: 'left', muted: true }
          ]}
          data={marketReturns}
        />

        <h4 className="font-semibold text-gray-900 mt-6 mb-3">Real Estate Appreciation</h4>
        <DataTable
          columns={[
            { key: 'market', header: 'Market', align: 'left' },
            { key: 'return', header: 'Return', align: 'right' },
            { key: 'source', header: 'Source', align: 'left', muted: true },
            { key: 'used', header: 'Used in', align: 'left', muted: true }
          ]}
          data={realEstateReturns}
        />

        <h4 className="font-semibold text-gray-900 mt-6 mb-3">Property-Specific Inputs</h4>
        <DataTable
          columns={[
            { key: 'input', header: 'Input', align: 'left' },
            { key: 'value', header: 'Value', align: 'right' },
            { key: 'notes', header: 'Source/Notes', align: 'left', muted: true }
          ]}
          data={propertyInputs}
        />
      </ReportSubsection>

      <ReportSubsection title="Limitations">
        <CalloutBox type="warning" title="Important Disclaimers">
          <ul className="space-y-2">
            <li>• This analysis is for educational purposes only and does not constitute financial, tax, or legal advice.</li>
            <li>• Past performance does not guarantee future results. Both real estate and stock markets can lose value.</li>
            <li>• Tax situations are complex and vary by individual. Consult a qualified tax professional for your specific situation.</li>
            <li>• Property-specific factors (condition, tenant quality, neighborhood changes) can significantly affect outcomes.</li>
            <li>• The model uses simplifying assumptions that may not reflect your actual experience.</li>
          </ul>
        </CalloutBox>
      </ReportSubsection>
    </ReportSection>
  );
};
